import { BubbleWideNotification } from '@owner/icons-vue/BubbleWideNotification'
import { FocusZoomIn } from '@owner/icons-vue/FocusZoomIn'
import { Phone } from '@owner/icons-vue/Phone'
import { SubscriptionStar } from '@owner/icons-vue/SubscriptionStar'
import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PlanComparisonSection, UpsellFeature } from '../types'

interface MaxUpsellContent {
  description: ComputedRef<string>
  features: ComputedRef<UpsellFeature[]>
  plans: ComputedRef<string[]>
  price: ComputedRef<string>
  sections: ComputedRef<PlanComparisonSection[]>
  tableCaption: ComputedRef<string>
  title: ComputedRef<string>
}

const CARD_ICONS = {
  app: Phone,
  reviews: SubscriptionStar,
  sms: BubbleWideNotification,
  upscale: FocusZoomIn,
} as const

/**
 * The shared placeholder "Max" plan marketing content (hero copy, feature
 * cards, and the Boost/Max comparison matrix) rendered by both the full-page
 * upsell takeover and the upsell modal. Copy lives in the global `upsell`
 * translations.
 */
export function useMaxUpsellContent(): MaxUpsellContent {
  const { t } = useI18n({ useScope: 'global' })

  const features = computed<UpsellFeature[]>(() =>
    (['app', 'sms', 'upscale', 'reviews'] as const).map((key) => ({
      description: t(`upsell.feature-cards.${key}.body`),
      icon: CARD_ICONS[key],
      title: t(`upsell.feature-cards.${key}.title`),
    }))
  )

  const plans = computed(() => [t('upsell.plan-boost'), t('upsell.plan-max')])

  const sections = computed<PlanComparisonSection[]>(() => [
    {
      rows: [
        {
          availability: [true, true],
          name: t('upsell.table-rows.website-improver'),
        },
        { availability: [false, true], name: t('upsell.table-rows.ai-cro') },
        { availability: [false, true], name: t('upsell.table-rows.upscaling') },
        {
          availability: [false, true],
          name: t('upsell.table-rows.review-replies'),
        },
      ],
      title: t('upsell.table-sections.ai'),
    },
    {
      rows: [
        { availability: [true, true], name: t('upsell.table-rows.seo') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.custom-website'),
        },
        { availability: [true, true], name: t('upsell.table-rows.auto-gbp') },
        { availability: [false, true], name: t('upsell.table-rows.job-board') },
        {
          availability: [false, true],
          name: t('upsell.table-rows.review-engine'),
        },
        { availability: [false, true], name: t('upsell.table-rows.listings') },
      ],
      title: t('upsell.table-sections.website'),
    },
    {
      rows: [
        {
          availability: [true, true],
          name: t('upsell.table-rows.online-ordering'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.order-with-google'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.live-tracking'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.delivery-support'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.catering-forms'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.reservations'),
        },
      ],
      title: t('upsell.table-sections.ordering'),
    },
    {
      rows: [
        { availability: [true, true], name: t('upsell.table-rows.auto-email') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.custom-email'),
        },
        { availability: [true, true], name: t('upsell.table-rows.promotions') },
        { availability: [true, true], name: t('upsell.table-rows.reputation') },
        { availability: [false, true], name: t('upsell.table-rows.auto-sms') },
        {
          availability: [false, true],
          name: t('upsell.table-rows.loyalty-integrations'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.cross-channel-loyalty'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.unified-inbox'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.push-marketing'),
        },
      ],
      title: t('upsell.table-sections.marketing'),
    },
    {
      rows: [
        {
          availability: [false, true],
          name: t('upsell.table-rows.branded-app'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.branded-gift-cards'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.branded-email'),
        },
      ],
      title: t('upsell.table-sections.brand'),
    },
  ])

  return {
    description: computed(() => t('upsell.description')),
    features,
    plans,
    price: computed(() => t('upsell.price')),
    sections,
    tableCaption: computed(() => t('upsell.table-caption')),
    title: computed(() => t('upsell.title')),
  }
}
